import { t } from "@lingui/macro";
import {
  ArrowCounterClockwise,
  ArrowRight,
  CaretDown,
  ChatTeardropText,
  CircleNotch,
  Exam,
  Lightning,
  PenNib,
  Sparkle,
  TextAa,
  TrendUp,
  WarningCircle,
} from "@phosphor-icons/react";
import { useBreakpoint } from "@reactive-resume/hooks";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Tooltip,
} from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";
import { AnimatePresence, motion } from "framer-motion";
import { useCallback, useRef, useState } from "react";

import { useFeatureGate } from "../hooks/use-feature-gate";
import { useTakingLonger } from "../hooks/use-taking-longer";
import { toast } from "../hooks/use-toast";
import { AiAction, AiTone, processAi, reportAiOutcome } from "../services/ai";
import { useAiStore } from "../stores/ai";

type ActiveAction = AiAction | "custom" | false;

const stripHtml = (html: string) => html.replace(/<[^>]*>/g, "").trim();

type Props = {
  value: string;
  onChange: (value: string) => void;
  className?: string;
  sectionType?: string;
  context?: Record<string, unknown>;
};

const toneOptions: { value: AiTone; label: () => string; emoji: string }[] = [
  { value: "casual", label: () => t`Casual`, emoji: "\u{1F642}" },
  { value: "professional", label: () => t`Professional`, emoji: "\u{1F4BC}" },
  { value: "confident", label: () => t`Confident`, emoji: "\u{1F60E}" },
  { value: "friendly", label: () => t`Friendly`, emoji: "\u{1F60A}" },
  { value: "concise", label: () => t`Concise`, emoji: "\u{26A1}" },
  { value: "enthusiastic", label: () => t`Enthusiastic`, emoji: "\u{1F525}" },
];

const quickActions = [
  { id: "improve-writing" as const, icon: PenNib, label: () => t`Improve`, tip: () => t`Enhance clarity, impact, and professionalism` },
  { id: "fix-grammar" as const, icon: Exam, label: () => t`Grammar`, tip: () => t`Fix spelling, grammar, and punctuation` },
  { id: "expand-content" as const, icon: TextAa, label: () => t`Expand`, tip: () => t`Add more detail and depth` },
  { id: "quantify-achievements" as const, icon: TrendUp, label: () => t`Quantify`, tip: () => t`Add example metrics in [brackets] you can edit to real numbers` },
];

export const AiActions = ({ value, onChange, className, sectionType, context }: Props) => {
  const [loading, setLoading] = useState<ActiveAction>(false);
  const takingLonger = useTakingLonger(!!loading);
  const [lastAction, setLastAction] = useState<string | null>(null);
  const [customPrompt, setCustomPrompt] = useState("");
  const [history, setHistory] = useState<string[]>([]);
  const promptRef = useRef<HTMLInputElement>(null);
  const { isMobile } = useBreakpoint();
  const { gate } = useFeatureGate();

  const setPanelOpen = useAiStore((s) => s.setPanelOpen);
  const addMessage = useAiStore((s) => s.addMessage);
  const setPendingSend = useAiStore((s) => s.setPendingSend);
  const setRateLimit = useAiStore((s) => s.setRateLimit);
  const rateLimit = useAiStore((s) => s.rateLimit);

  const pushHistory = useCallback((val: string) => {
    setHistory((prev) => [...prev.slice(-9), val]);
  }, []);

  const handleAction = useCallback(
    async (action: AiAction, extra?: { tone?: AiTone }) => {
      const basicActions: AiAction[] = ["improve-writing", "fix-grammar", "change-tone"];
      const featureKey = basicActions.includes(action) ? "ai-basic" : "ai-full";
      if (!gate(featureKey as "ai-basic" | "ai-full")) return;

      if (!stripHtml(value) && !["generate-summary", "generate-experience", "generate-section"].includes(action)) {
        toast({
          variant: "info",
          title: t`No content to process`,
          description: t`Write some content first, then use AI to refine it.`,
        });
        return;
      }

      if (rateLimit?.blocked) {
        toast({
          variant: "error",
          title: t`Daily limit reached`,
          description: t`You've used all your free AI requests for today. Your limit resets automatically. Check back tomorrow!`,
        });
        return;
      }

      try {
        setLoading(action);
        setLastAction(action);
        pushHistory(value);

        const response = await processAi({
          action,
          content: value,
          tone: extra?.tone,
          sectionType,
          context,
        });

        if (response.rateLimit) {
          setRateLimit(response.rateLimit);
          if (response.rateLimit.warning) {
            toast({
              variant: "warning",
              title: t`Usage notice`,
              description: response.rateLimit.warning,
            });
          }
        }

        if (response.ok === false) {
          setHistory((prev) => prev.slice(0, -1));
          toast({
            variant: "error",
            title: t`Please try again`,
            description: response.result,
          });
          return;
        }

        if (response.result && response.result !== value) {
          onChange(response.result);
          reportAiOutcome(response.callId, "used");
        }
      } catch (error) {
        setHistory((prev) => prev.slice(0, -1));
        toast({
          variant: "error",
          title: t`Oops, the server returned an error.`,
          description: (error as Error)?.message,
        });
      } finally {
        setLoading(false);
      }
    },
    [value, onChange, sectionType, context, pushHistory, rateLimit, setRateLimit],
  );

  const handleRevert = useCallback(() => {
    if (history.length === 0) return;
    const prev = history[history.length - 1];
    setHistory((h) => h.slice(0, -1));
    onChange(prev);
    if (history.length <= 1) setLastAction(null);
    toast({
      variant: "success",
      title: t`Reverted`,
      description: history.length > 1
        ? t`Content restored. You can revert again.`
        : t`Content restored to original.`,
    });
  }, [history, onChange]);

  const openInChat = useCallback((prompt?: string) => {
    const plainText = stripHtml(value);
    const desc = sectionType ? `my ${sectionType} section` : "this content";
    const contextContent = plainText
      ? `Here is my current ${desc}:\n\n"""${plainText}"""`
      : `I have an empty ${desc}.`;

    addMessage({
      id: `dialog-ctx-${Date.now()}`,
      role: "user",
      content: contextContent,
      timestamp: Date.now(),
      type: "context",
      contextLabel: plainText
        ? t`${sectionType ?? "Content"} loaded · ${plainText.split(/\s+/).length} words`
        : t`${sectionType ?? "Section"} · empty`,
    });

    const userPrompt = prompt?.trim()
      ? prompt.trim()
      : plainText
        ? `Help me improve ${desc}. Give me specific, actionable suggestions.`
        : `Help me write content for ${desc}. What would you suggest?`;

    setPanelOpen(true);
    setPendingSend(userPrompt);
  }, [value, sectionType, addMessage, setPanelOpen, setPendingSend]);

  const handleCustomPrompt = useCallback(() => {
    if (!customPrompt.trim()) return;
    openInChat(customPrompt);
    setCustomPrompt("");
  }, [customPrompt, openInChat]);

  const onPromptKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleCustomPrompt();
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.1 }}
      className={cn("mt-3", className)}
    >
      <div className="overflow-hidden rounded-xl border border-border/40 bg-gradient-to-b from-background to-secondary-accent/15">
        {/* Quick-polish actions */}
        <div className="flex flex-wrap items-center gap-1 px-2 pt-2 pb-1.5">
          {quickActions.map((a) => (
            <Tooltip key={a.id} content={a.tip()}>
              <button
                type="button"
                disabled={!!loading}
                onClick={() => handleAction(a.id)}
                className={cn(
                  "flex h-7 items-center gap-1.5 rounded-lg px-2 text-[11px] font-medium transition-all",
                  "bg-secondary-accent/50 text-foreground/70",
                  "hover:bg-primary/10 hover:text-primary",
                  "disabled:pointer-events-none disabled:opacity-40",
                  lastAction === a.id && !loading && "bg-primary/10 text-primary ring-1 ring-primary/20",
                )}
              >
                {loading === a.id
                  ? <CircleNotch size={12} className="animate-spin" />
                  : <a.icon size={12} />}
                {!isMobile && <span>{a.label()}</span>}
              </button>
            </Tooltip>
          ))}

          <DropdownMenu>
            <Tooltip content={t`Change tone and style`}>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  disabled={!!loading}
                  className={cn(
                    "flex h-7 items-center gap-1 rounded-lg px-2 text-[11px] font-medium transition-all",
                    "bg-secondary-accent/50 text-foreground/70",
                    "hover:bg-primary/10 hover:text-primary",
                    "disabled:pointer-events-none disabled:opacity-40",
                  )}
                >
                  {loading === "change-tone"
                    ? <CircleNotch size={12} className="animate-spin" />
                    : <ChatTeardropText size={12} />}
                  {!isMobile && <span>{t`Tone`}</span>}
                  <CaretDown size={8} className="opacity-40" />
                </button>
              </DropdownMenuTrigger>
            </Tooltip>
            <DropdownMenuContent align="start" className="min-w-[140px]">
              {toneOptions.map((opt) => (
                <DropdownMenuItem key={opt.value} onClick={() => handleAction("change-tone", { tone: opt.value })}>
                  <span className="mr-2 text-sm" role="img">{opt.emoji}</span>
                  <span className="text-xs">{opt.label()}</span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {takingLonger && (
          <p className="px-2.5 pb-1 text-[10px] text-muted-foreground">{t`Taking longer than usual…`}</p>
        )}

        {/* Divider */}
        <div className="mx-2 border-t border-border/25" />

        {/* Conversational input */}
        <div className="flex items-center gap-2 px-2.5 py-2">
          <Sparkle size={13} weight="fill" className="shrink-0 text-primary/30" />
          <input
            ref={promptRef}
            value={customPrompt}
            onChange={(e) => setCustomPrompt(e.target.value)}
            onKeyDown={onPromptKeyDown}
            disabled={!!loading}
            placeholder={t`Ask AI anything...`}
            className="min-w-0 flex-1 bg-transparent text-xs outline-none placeholder:text-muted-foreground/40"
          />
          <button
            type="button"
            onClick={handleCustomPrompt}
            disabled={!customPrompt.trim() || !!loading}
            className={cn(
              "flex h-5 w-5 shrink-0 items-center justify-center rounded-md transition-all",
              customPrompt.trim()
                ? "bg-primary text-primary-foreground shadow-sm hover:bg-primary/90"
                : "text-muted-foreground/20",
            )}
          >
            {loading === "custom"
              ? <CircleNotch size={10} className="animate-spin" />
              : <ArrowRight size={10} weight="bold" />}
          </button>
        </div>
      </div>

      {/* Revert feedback */}
      <AnimatePresence>
        {lastAction && !loading && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="mt-1.5 flex items-center gap-2 rounded-lg border border-primary/10 bg-primary/[0.03] px-2.5 py-1.5">
              <Lightning size={11} weight="fill" className="shrink-0 text-primary/70" />
              <span className="flex-1 text-[10px] font-medium text-foreground/60">
                {lastAction === "quantify-achievements" || lastAction === "improve-writing"
                  ? t`Numbers in [brackets] are examples. Replace them with your real figures before you send.`
                  : t`AI applied`}
              </span>

              {history.length > 0 && (
                <Tooltip content={history.length > 1 ? t`Undo last AI change (${history.length} steps)` : t`Undo AI change`}>
                  <button
                    type="button"
                    onClick={handleRevert}
                    className="flex items-center gap-1 rounded-md bg-background px-2 py-0.5 text-[10px] font-semibold text-orange-600 shadow-sm ring-1 ring-orange-200 transition-all hover:bg-orange-50 hover:ring-orange-300 dark:text-orange-400 dark:ring-orange-800 dark:hover:bg-orange-950/30"
                  >
                    <ArrowCounterClockwise size={9} weight="bold" />
                    <span>{t`Revert`}{history.length > 1 ? ` (${history.length})` : ""}</span>
                  </button>
                </Tooltip>
              )}

              <button
                type="button"
                className="text-[10px] text-muted-foreground/40 transition-colors hover:text-muted-foreground"
                onClick={() => { setLastAction(null); setHistory([]); }}
              >
                {t`Dismiss`}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Rate limit warning */}
      <AnimatePresence>
        {rateLimit?.warning && !rateLimit.blocked && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <div className="mt-1 flex items-center gap-1.5 rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-1 dark:border-amber-800 dark:bg-amber-950/30">
              <WarningCircle size={11} className="shrink-0 text-amber-600 dark:text-amber-400" />
              <span className="text-[10px] text-amber-700 dark:text-amber-300">{rateLimit.warning}</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};
